import type { PersistedCardSolverConfig } from '@/domain/state/cardSolverConfigs'

export interface CardRouteBadgeData {
  cards: PersistedCardSolverConfig[]
  count: number
}

function normalizeAddress(address: unknown): string | null {
  return typeof address === 'string' && address.length > 0 ? address.toLowerCase() : null
}

function normalizeSubAccountId(subAccountId: unknown): number | null {
  return typeof subAccountId === 'number' ? subAccountId : null
}

function buildBadgeData(cards: PersistedCardSolverConfig[]): CardRouteBadgeData | null {
  if (cards.length === 0) return null
  return {
    cards,
    count: cards.length,
  }
}

export function getPooledBorrowCardRouteBadge(
  configs: PersistedCardSolverConfig[],
  borrowTokenAddress: string | undefined,
): CardRouteBadgeData | null {
  const tokenAddress = normalizeAddress(borrowTokenAddress)
  if (!tokenAddress) return null

  return buildBadgeData(
    configs.filter(
      (config) =>
        config.solverType === 'hypurrFi' &&
        normalizeAddress(config.cardSolverConfig.borrowTokenAddress) === tokenAddress,
    ),
  )
}

export function getPooledSpendCardRouteBadge(
  configs: PersistedCardSolverConfig[],
  aTokenAddress: string | undefined,
): CardRouteBadgeData | null {
  const tokenAddress = normalizeAddress(aTokenAddress)
  if (!tokenAddress) return null

  return buildBadgeData(
    configs.filter(
      (config) =>
        config.solverType === 'hypurrFi-withdraw' &&
        normalizeAddress(config.cardSolverConfig.aTokenAddress) === tokenAddress,
    ),
  )
}

export function getEulerBorrowCardRouteBadge(
  configs: PersistedCardSolverConfig[],
  params: {
    borrowVaultAddress: string | undefined
    subAccountId?: number | null
  },
): CardRouteBadgeData | null {
  const borrowVaultAddress = normalizeAddress(params.borrowVaultAddress)
  if (!borrowVaultAddress) return null

  return buildBadgeData(
    configs.filter((config) => {
      if (config.solverType !== 'euler') return false
      if (normalizeAddress(config.cardSolverConfig.borrowVaultAddress) !== borrowVaultAddress) return false

      const configSubAccountId = normalizeSubAccountId(config.cardSolverConfig.subAccountId)
      return params.subAccountId == null || configSubAccountId == null || configSubAccountId === params.subAccountId
    }),
  )
}

export function getEulerSpendCardRouteBadge(
  configs: PersistedCardSolverConfig[],
  params: {
    supplyVaultAddress: string | undefined
    subAccountId?: number | null
  },
): CardRouteBadgeData | null {
  const supplyVaultAddress = normalizeAddress(params.supplyVaultAddress)
  if (!supplyVaultAddress) return null

  return buildBadgeData(
    configs.filter((config) => {
      if (config.solverType !== 'euler-withdraw') return false

      const configSupplyVaultAddress =
        normalizeAddress(config.cardSolverConfig.supplyVaultAddress) ??
        normalizeAddress(config.cardSolverConfig.borrowVaultAddress)
      if (configSupplyVaultAddress !== supplyVaultAddress) return false

      const configSubAccountId = normalizeSubAccountId(config.cardSolverConfig.subAccountId)
      return params.subAccountId == null || configSubAccountId == null || configSubAccountId === params.subAccountId
    }),
  )
}
